import { SignIn, SignUp, useSignIn } from "@clerk/clerk-react";
import { ArrowUpIcon, KeyRoundIcon } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useEffect, useRef, useState } from "react";
import {
	clerkErrorMessage,
	isUserCancelledError,
} from "./clerk-errors.js";
import { matrixClerkAppearance } from "./clerk-appearance.js";
import { MairkoMark } from "./MairkoMark.js";
import { PlatformMark } from "./PlatformMark.js";
import type { ResolvedTheme } from "./theme.js";

type Props = {
	resolved: ResolvedTheme;
};

type AuthMode = "sign-in" | "sign-up";

const RATE_LIMIT_HINT = "Too many requests";
const COOLDOWN_MS = 12_000;

function modeFromHash(): AuthMode {
	const hash = window.location.hash.toLowerCase();
	if (hash.includes("sign-up") || hash.includes("signup")) return "sign-up";
	return "sign-in";
}

function normalizeAuthHash(mode: AuthMode): void {
	const clean = mode === "sign-up" ? "#sign-up" : "#sign-in";
	if (window.location.hash !== clean) {
		window.history.replaceState(null, "", `${window.location.pathname}${clean}`);
	}
}

function PasskeySignInButton({ disabled }: { disabled?: boolean }) {
	const { isLoaded, signIn, setActive } = useSignIn();
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const supported =
		typeof window !== "undefined" &&
		typeof window.PublicKeyCredential !== "undefined";

	if (!isLoaded || !supported || !signIn) return null;

	return (
		<div className="auth-passkey-wrap">
			<button
				type="button"
				className="auth-passkey"
				disabled={busy || disabled}
				onClick={() => {
					void (async () => {
						setBusy(true);
						setError(null);
						try {
							const attempt = await signIn.authenticateWithPasskey({
								flow: "discoverable",
							});
							if (
								attempt.status === "complete" &&
								attempt.createdSessionId
							) {
								await setActive({ session: attempt.createdSessionId });
							}
						} catch (err) {
							if (isUserCancelledError(err)) return;
							const message = clerkErrorMessage(err);
							if (message) setError(message);
						} finally {
							setBusy(false);
						}
					})();
				}}
			>
				<KeyRoundIcon className="size-3.5" aria-hidden />
				{busy ? "Waiting for passkey…" : "Sign in with passkey"}
			</button>
			{error && (
				<p className="auth-passkey-error" role="alert">
					{error}
				</p>
			)}
		</div>
	);
}

function RateLimitBanner({
	secondsLeft,
	onRetry,
}: {
	secondsLeft: number;
	onRetry: () => void;
}) {
	const ready = secondsLeft <= 0;
	return (
		<div className="auth-rate-limit" role="status">
			<p className="auth-rate-limit-title">Slow down for a moment</p>
			<p className="auth-rate-limit-copy">
				Clerk limits sign-in and sign-up attempts (about 5 every 10 seconds).
				Refreshing the page counts toward that.
			</p>
			{ready ? (
				<button
					type="button"
					className="auth-rate-limit-retry"
					onClick={onRetry}
				>
					Try again
				</button>
			) : (
				<p className="auth-rate-limit-wait">
					Try again in {secondsLeft}s
				</p>
			)}
		</div>
	);
}

export function SignInScreen({ resolved }: Props) {
	const appearance = matrixClerkAppearance(resolved);
	const [mode, setMode] = useState<AuthMode>(() => modeFromHash());
	const isSignUp = mode === "sign-up";

	// Mount each Clerk form once — remounting on every toggle/refresh creates
	// /v1/sign_ins|/v1/sign_ups and burns the 5/10s Frontend API budget.
	const [mountedSignIn, setMountedSignIn] = useState(() => !isSignUp);
	const [mountedSignUp, setMountedSignUp] = useState(() => isSignUp);
	const [signInKey, setSignInKey] = useState(0);
	const [signUpKey, setSignUpKey] = useState(0);

	const [cooldownUntil, setCooldownUntil] = useState<number | null>(null);
	const [secondsLeft, setSecondsLeft] = useState(0);
	const formSlotRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		normalizeAuthHash(mode);
		if (mode === "sign-in") setMountedSignIn(true);
		else setMountedSignUp(true);
	}, [mode]);

	useEffect(() => {
		const sync = () => setMode(modeFromHash());
		window.addEventListener("hashchange", sync);
		return () => window.removeEventListener("hashchange", sync);
	}, []);

	useEffect(() => {
		const root = formSlotRef.current;
		if (!root) return;

		const scan = () => {
			const text = root.textContent ?? "";
			if (text.includes(RATE_LIMIT_HINT)) {
				setCooldownUntil((prev) => prev ?? Date.now() + COOLDOWN_MS);
			}
		};

		scan();
		const obs = new MutationObserver(scan);
		obs.observe(root, {
			childList: true,
			subtree: true,
			characterData: true,
		});
		return () => obs.disconnect();
	}, [signInKey, signUpKey, mode]);

	useEffect(() => {
		if (cooldownUntil === null) {
			setSecondsLeft(0);
			return;
		}
		const tick = () => {
			const left = Math.max(0, Math.ceil((cooldownUntil - Date.now()) / 1000));
			setSecondsLeft(left);
			if (left <= 0) {
				/* keep banner until explicit retry */
			}
		};
		tick();
		const id = window.setInterval(tick, 250);
		return () => window.clearInterval(id);
	}, [cooldownUntil]);

	const switchMode = (next: AuthMode) => {
		if (next === mode) return;
		setMode(next);
	};

	const retryAfterCooldown = () => {
		setCooldownUntil(null);
		if (mode === "sign-up") setSignUpKey((k) => k + 1);
		else setSignInKey((k) => k + 1);
	};

	const rateLimited = cooldownUntil !== null;
	const reduceMotion = useReducedMotion();
	const [splitTraveling, setSplitTraveling] = useState(false);
	const splitMove = reduceMotion ? { duration: 0 } : AUTH_SPLIT_MOVE;
	const markTravelStart = () => {
		if (!reduceMotion) setSplitTraveling(true);
	};
	const markTravelEnd = () => setSplitTraveling(false);

	return (
		<div className="auth-stage" data-theme={resolved}>
			<div
				className="auth-split"
				data-layout={isSignUp ? "signup" : "signin"}
				data-traveling={splitTraveling ? "true" : "false"}
			>
				<motion.div
					className="auth-split-form"
					layout={!reduceMotion}
					transition={{
						...splitMove,
						delay: reduceMotion ? 0 : 0.1,
					}}
					onLayoutAnimationStart={markTravelStart}
					onLayoutAnimationComplete={markTravelEnd}
				>
					<div className="auth-split-form-inner">
						<MairkoMark size="boot" shimmer="off" />
						<div className="auth-simple-title-slot">
						<AnimatePresence mode="wait" initial={false}>
							<motion.h1
								key={isSignUp ? "sign-up" : "sign-in"}
								className="auth-simple-title"
								initial={
									reduceMotion
										? false
										: { opacity: 0, y: 12, filter: "blur(8px)" }
								}
								animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
								exit={
									reduceMotion
										? undefined
										: { opacity: 0, y: -10, filter: "blur(8px)" }
								}
								transition={AUTH_SPLIT_COPY}
							>
								{isSignUp ? "Create your account." : "Log in."}
							</motion.h1>
						</AnimatePresence>
						</div>

						{rateLimited && (
							<RateLimitBanner
								secondsLeft={secondsLeft}
								onRetry={retryAfterCooldown}
							/>
						)}

						<div
							ref={formSlotRef}
							className="auth-form-slot"
							hidden={rateLimited && secondsLeft > 0}
						>
							{mountedSignIn && (
								<div
									className="auth-form"
									hidden={isSignUp}
									aria-hidden={isSignUp}
								>
									<PasskeySignInButton disabled={rateLimited} />
									<SignIn
										key={`sign-in-${signInKey}`}
										routing="virtual"
										fallbackRedirectUrl="/"
										appearance={appearance}
									/>
								</div>
							)}
							{mountedSignUp && (
								<div
									className="auth-form"
									hidden={!isSignUp}
									aria-hidden={!isSignUp}
								>
									<SignUp
										key={`sign-up-${signUpKey}`}
										routing="virtual"
										fallbackRedirectUrl="/"
										appearance={appearance}
									/>
								</div>
							)}
						</div>

						<p className="auth-switch">
							{isSignUp ? (
								<>
									Already have an account?{" "}
									<button
										type="button"
										className="auth-switch-link"
										onClick={() => switchMode("sign-in")}
									>
										Log in
									</button>
								</>
							) : (
								<>
									Don&apos;t have an account?{" "}
									<button
										type="button"
										className="auth-switch-link"
										onClick={() => switchMode("sign-up")}
									>
										Create your account
									</button>
								</>
							)}
						</p>
					</div>
				</motion.div>
				<AuthProductPanel
					layoutMotion={!reduceMotion}
					onTravelStart={markTravelStart}
					onTravelEnd={markTravelEnd}
				/>
			</div>
		</div>
	);
}

const AUTH_SPLIT_MOVE = {
	type: "tween" as const,
	duration: 1.2,
	ease: [0.16, 1, 0.3, 1] as const,
};

const AUTH_SPLIT_COPY = {
	type: "tween" as const,
	duration: 0.42,
	ease: [0.22, 1, 0.36, 1] as const,
};

const AUTH_PREFIX = "Ask Mairko ";

const AUTH_BEATS = [
	{
		suffix: "to pause weak Search campaigns.",
		kicker: "Google Ads · GA4",
		title: "Pause three Search campaigns",
		metrics: [
			{ value: "3", label: "to pause" },
			{ value: "Search", label: "network" },
			{ value: "GA4", label: "proof" },
		],
		tools: [{ markId: "ads" }, { markId: "analytics" }],
	},
	{
		suffix: "to run Search and Meta together.",
		kicker: "Google Ads · Meta Ads",
		title: "One plan. Both networks.",
		metrics: [
			{ value: "2", label: "networks" },
			{ value: "Gate", label: "before spend" },
		],
		tools: [{ markId: "ads" }, { markId: "meta" }],
	},
	{
		suffix: "to ship a homepage change.",
		kicker: "WordPress · working copy",
		title: "Homepage draft is ready",
		metrics: [
			{ value: "1", label: "page" },
			{ value: "You", label: "publish" },
		],
		tools: [{ markId: "wordpress" }],
	},
	{
		suffix: "to book the local paper ads.",
		kicker: "Business Profile · Google Ads",
		title: "Inserts for the zips that convert",
		metrics: [
			{ value: "2", label: "markets" },
			{ value: "4", label: "papers" },
		],
		tools: [{ markId: "gbp" }, { markId: "ads" }],
	},
	{
		suffix: "to send this week’s ads to Slack.",
		kicker: "Google Ads · Meta · Slack",
		title: "This week’s ads need a yes",
		metrics: [
			{ value: "Slack", label: "channel" },
			{ value: "1st yes", label: "wins" },
		],
		tools: [{ markId: "ads" }, { markId: "meta" }, { markId: "slack" }],
	},
] as const;

type AuthBeat = (typeof AUTH_BEATS)[number];
type AskPhase = "typing" | "hold" | "deleting";

function AuthAskResult({
	beat,
	reduceMotion,
}: {
	beat: AuthBeat;
	reduceMotion: boolean | null;
}) {
	return (
		<motion.div
			className="auth-split-result"
			initial={reduceMotion ? false : { opacity: 0, y: 14, scale: 0.98 }}
			animate={{ opacity: 1, y: 0, scale: 1 }}
			exit={reduceMotion ? undefined : { opacity: 0, y: 8, scale: 0.98 }}
			transition={{ duration: 0.42, ease: [0.16, 1, 0.3, 1] }}
		>
			<div className="auth-split-result-head">
				<div className="auth-split-result-marks" aria-hidden>
					{beat.tools.map((tool, index) => (
						<motion.span
							key={tool.markId}
							className="auth-split-result-mark"
							style={{ zIndex: beat.tools.length - index }}
							initial={
								reduceMotion ? false : { opacity: 0, scale: 0.7, x: -6 }
							}
							animate={{ opacity: 1, scale: 1, x: 0 }}
							transition={{
								delay: reduceMotion ? 0 : 0.08 + index * 0.06,
								duration: 0.34,
								ease: [0.22, 1, 0.36, 1],
							}}
						>
							<PlatformMark markId={tool.markId} size={16} />
						</motion.span>
					))}
				</div>
				<div className="auth-split-result-copy">
					<p className="auth-split-result-kicker">{beat.kicker}</p>
					<p className="auth-split-result-title">{beat.title}</p>
				</div>
			</div>
			<dl className="auth-split-result-metrics">
				{beat.metrics.map((metric, index) => (
					<motion.div
						key={metric.label}
						className="auth-split-result-metric"
						initial={reduceMotion ? false : { opacity: 0, y: 6 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{
							delay: reduceMotion ? 0 : 0.18 + index * 0.06,
							duration: 0.3,
							ease: [0.22, 1, 0.36, 1],
						}}
					>
						<dt>{metric.label}</dt>
						<dd>{metric.value}</dd>
					</motion.div>
				))}
			</dl>
		</motion.div>
	);
}

function wrapIndex(index: number, length: number): number {
	return ((index % length) + length) % length;
}

const AUTH_ATMOSPHERE_STILL = "/auth/atmosphere.jpg";
const AUTH_ATMOSPHERE_LOOP = "/auth/atmosphere.mp4";

function AuthAtmosphere({ reduceMotion }: { reduceMotion: boolean | null }) {
	const videoRef = useRef<HTMLVideoElement>(null);
	const [loopReady, setLoopReady] = useState(false);

	useEffect(() => {
		const video = videoRef.current;
		if (!video) return;
		if (reduceMotion) {
			video.pause();
			return;
		}
		void video.play().catch(() => {
			/* Autoplay can wait — the still stays up. */
		});
	}, [loopReady, reduceMotion]);

	return (
		<div className="auth-split-atmosphere" aria-hidden>
			<img
				className="auth-split-atmosphere-still"
				src={AUTH_ATMOSPHERE_STILL}
				alt=""
				draggable={false}
			/>
			<video
				ref={videoRef}
				className="auth-split-atmosphere-loop"
				data-ready={loopReady ? "true" : "false"}
				poster={AUTH_ATMOSPHERE_STILL}
				src={AUTH_ATMOSPHERE_LOOP}
				muted
				loop
				playsInline
				autoPlay
				preload="auto"
				onCanPlay={() => setLoopReady(true)}
				onPlaying={() => setLoopReady(true)}
				onError={() => setLoopReady(false)}
			/>
			<div className="auth-split-atmosphere-grain" />
			<div className="auth-split-atmosphere-vignette" />
		</div>
	);
}

/** Product stage — login keeps it on the right; sign-up slides it left. */
export function AuthProductPanel({
	layoutMotion = false,
	onTravelStart,
	onTravelEnd,
}: {
	layoutMotion?: boolean;
	onTravelStart?: () => void;
	onTravelEnd?: () => void;
}) {
	const reduceMotion = useReducedMotion();
	const [beat, setBeat] = useState(0);
	const [phase, setPhase] = useState<AskPhase>("typing");
	const [chars, setChars] = useState(0);
	// beat is always wrapped, but noUncheckedIndexedAccess can't see that.
	const activeBeat = AUTH_BEATS[beat] ?? AUTH_BEATS[0];
	const suffix = activeBeat.suffix;

	useEffect(() => {
		if (reduceMotion) {
			setChars(suffix.length);
			setPhase("hold");
			const id = window.setInterval(() => {
				setBeat((prev) => wrapIndex(prev + 1, AUTH_BEATS.length));
			}, 4000);
			return () => window.clearInterval(id);
		}

		if (phase === "typing") {
			if (chars >= suffix.length) {
				const id = window.setTimeout(() => setPhase("hold"), 60);
				return () => window.clearTimeout(id);
			}
			const delay = suffix[chars] === " " ? 52 : 30;
			const id = window.setTimeout(() => setChars((n) => n + 1), delay);
			return () => window.clearTimeout(id);
		}

		if (phase === "hold") {
			const id = window.setTimeout(() => setPhase("deleting"), 3800);
			return () => window.clearTimeout(id);
		}

		if (chars <= 0) {
			const id = window.setTimeout(() => {
				setBeat((prev) => wrapIndex(prev + 1, AUTH_BEATS.length));
				setPhase("typing");
			}, 180);
			return () => window.clearTimeout(id);
		}

		const id = window.setTimeout(() => setChars((n) => n - 1), 14);
		return () => window.clearTimeout(id);
	}, [beat, chars, phase, reduceMotion, suffix]);

	const typed = suffix.slice(0, chars);
	const showReply = phase === "hold";

	return (
		<motion.aside
			className="auth-split-panel"
			layout={layoutMotion}
			transition={reduceMotion ? { duration: 0 } : AUTH_SPLIT_MOVE}
			onLayoutAnimationStart={onTravelStart}
			onLayoutAnimationComplete={onTravelEnd}
			aria-hidden
		>
			<div className="auth-split-canvas">
				<AuthAtmosphere reduceMotion={reduceMotion} />
				<div className="auth-split-demo">
					<div className="auth-split-ask">
						<p className="auth-split-ask-copy">
							{AUTH_PREFIX}
							<span className="auth-split-ask-typed">{typed}</span>
							<span
								className={
									phase === "deleting"
										? "auth-split-caret auth-split-caret--steady"
										: "auth-split-caret"
								}
							/>
						</p>
						<span className="auth-split-ask-send">
							<ArrowUpIcon className="size-4" strokeWidth={2.25} />
						</span>
					</div>
					<AnimatePresence initial={false}>
						{showReply ? (
							<AuthAskResult
								key={activeBeat.title}
								beat={activeBeat}
								reduceMotion={reduceMotion}
							/>
						) : null}
					</AnimatePresence>
				</div>
			</div>
		</motion.aside>
	);
}
